import { get, post, put, remove } from '@/helpers/http';
import { SortType } from '@/transformers/sort-by';
import { Job } from '@/types/Job';
import { MeetingMinutesResult, MeetingMinutesView } from '@/types/MeetingMinutes';
import {
  Media,
  Media$Privacy,
  Media$Type,
  Media$Details,
  Media$Availability,
  Media$Version,
  Media$Quality,
  Media$AudioTrack,
  Media$AudioTrack$AddRequest,
  TrimInfo,
  ClipInfo,
} from '@/types/Media';
import { TimedataAbbreviation } from '@Ravnur-Inc/shared/video-editors/useTimedataEditor';
import { Timedata, TimedataSegment, TimedataThumbnail } from '@Ravnur-Inc/shared/types/Media';

import BaseRepository, { PagedListResponse } from './base-repository';
import { OperationError, Operation$Status, OperationEntity } from '@/types/Operation';
import axios from 'axios';
import { decodeVtt } from '@Ravnur-Inc/helpers/vtt';

export interface MediaQueryParams {
  q: string;
  type: Media$Type;
  privacy: Media$Privacy | Media$Privacy[];
  availability: Media$Availability;
  sortField: keyof Media;
  sortType: SortType;
  sort: string;
  owner: string;
  offset: number;
  count: number;

  folderId: string;
  groupId: string;
  exceptGroup: boolean;
  loadEmptyPlaylists?: boolean; // true by default
  hasAudioTracks?: string;
}

if (process.env.VUE_APP_MOCK_API === 'yes') {
  (function _bootstrap() {
    const adapter = require('../mocks/index').default;

    const fnc = require('../mocks/media').default;
    fnc(adapter);
  })();
}

export default class MediaRepository extends BaseRepository<
  Media$Details,
  MediaQueryParams,
  PagedListResponse<Media>
> {
  constructor() {
    super('/media');
  }

  public async versions({ id }: Media): Promise<{
    versions: Media$Version[];
    qualities: Media$Quality[];
  }> {
    return get(`${this._resource}/${id}/version`);
  }

  public audioTracks(mediaId: string): Promise<Media$AudioTrack[]> {
    return get(`${this._resource}/${mediaId}/tracks`);
  }

  public async addAudioTrack(
    mediaId: string,
    payload: Media$AudioTrack$AddRequest
  ): Promise<OperationEntity<Media$AudioTrack>> {
    const op: OperationEntity<Media$AudioTrack> = await post(
      `${this._resource}/${mediaId}/tracks`,
      payload
    );
    if (op.state === Operation$Status.FAILED) {
      throw new OperationError(op);
    }
    return op;
  }

  public deleteAudioTrack(mediaId: string, trackId: string) {
    return remove(`${this._resource}/${mediaId}/tracks/${trackId}`);
  }

  public updateAudioTrack(
    mediaId: string,
    trackId: string,
    title: string
  ): Promise<Media$AudioTrack> {
    return put(`${this._resource}/${mediaId}/tracks/${trackId}`, { title });
  }

  public verifyAudioTrack(mediaId: string, trackId: string) {
    return post(`${this._resource}/${mediaId}/tracks/${trackId}/verify`, {});
  }

  public verifyAllAudioTracks(mediaId: string) {
    return post(`${this._resource}/${mediaId}/tracks/verifyall`, {});
  }

  public async processAudioTrack(mediaId: string, trackId: string): Promise<OperationEntity<Job>> {
    const op: OperationEntity<Job> = await post(
      `${this._resource}/${mediaId}/tracks/${trackId}/process`,
      {}
    );
    if (op.state === Operation$Status.FAILED) {
      throw new OperationError(op);
    }
    return op;
  }

  public async undoUploading(mediaId: string, options: Dictionary<unknown> = {}) {
    return this.undo(mediaId, 'upload', options);
  }

  public uploadThumbnail(file: File, { id }: Media) {
    return this._upload(file, `${id}/thumbnail`);
  }

  public process(mediaId: string, versionId?: string) {
    const param = versionId ? `?versionId=${versionId}` : ``;
    return post(`${this._resource}/${mediaId}/process` + param, {});
  }

  public async cancelUpload(files: Pick<Media, 'id'>[]) {
    return post(`${this._resource}/purge`, files);
  }

  public cancelJob(mediaId: string) {
    return remove(`${this._resource}/${mediaId}/job`);
  }

  public trimVideo(id: string, info: TrimInfo) {
    return this.action({ id }, 'job/trimming', info);
  }

  public makeClip(id: string, info: ClipInfo) {
    return this.action({ id }, 'job/clipping', info);
  }

  public getTimedata(mediaId: string) {
    return get(`${this._resource}/${mediaId}/cc`);
  }

  public async getTimedataByLanguage(
    mediaId: string,
    language: string,
    timedataAbr: TimedataAbbreviation
  ): Promise<Timedata> {
    return get(`${this._resource}/${mediaId}/${timedataAbr}/${language}`);
  }

  public updateTimedata(
    mediaId: string,
    language: string,
    segments: TimedataSegment[],
    timedataAbr: TimedataAbbreviation
  ) {
    return put(`${this._resource}/${mediaId}/${timedataAbr}/${language}`, segments);
  }

  public toggleTimedataState(
    mediaId: string,
    language: string,
    state: string,
    timedataAbr: TimedataAbbreviation
  ) {
    return post(`${this._resource}/${mediaId}/${timedataAbr}/${language}/${state}`, {});
  }

  public getTranscriptByLanguage(mediaId: string, language: string): Promise<string> {
    return get(`${this._resource}/${mediaId}/transcript/${language}`);
  }

  public createNewCuePoint(mediaId: string, language: string, data: Partial<TimedataSegment>[]) {
    return post(`${this._resource}/${mediaId}/cp/${language}`, data);
  }

  public deleteTimedata(mediaId: string, language: string, timedataAbr: TimedataAbbreviation) {
    return remove(`${this._resource}/${mediaId}/${timedataAbr}/${language}`);
  }

  public generateCC(mediaId: string, language: string) {
    return post(`${this._resource}/${mediaId}/index/${language}`, {});
  }

  public transfer(
    userName: string,
    oldUserName: string,
    mediaIds: string[],
    exceptMediaIds: string[] = [],
    transferAll: boolean
  ) {
    const path = transferAll ? 'transfer' : 'owner';
    const data = transferAll ? { oldUserName, userName, exceptMediaIds } : { userName, mediaIds };
    return post(`/tools${this._resource}/${path}`, data);
  }

  public getAudioIndexingJobs(mediaId: string): Promise<Job[]> {
    return get(`${this._resource}/${mediaId}/job`);
  }

  public async generateAiDescription(mediaId: string): Promise<{ description: string }> {
    const response: OperationEntity<{ description: string }> = await get(
      `${this._resource}/${mediaId}/aidescription`
    );

    if (response.state === Operation$Status.FAILED) {
      throw new Error(response.errorReason[0]);
    } else {
      return response.data;
    }
  }

  public async generateAiChapters(mediaId: string): Promise<Timedata> {
    const response: OperationEntity<Timedata> = await get(
      `${this._resource}/${mediaId}/aichapters`
    );

    if (response.state === Operation$Status.FAILED) {
      throw new Error(response.errorReason[0]);
    } else {
      return response.data;
    }
  }

  public generateMeetingMinutes(mediaId: string): Promise<OperationEntity<Job>> {
    return this.action({ id: mediaId }, 'meeting-minutes') as Promise<OperationEntity<Job>>;
  }

  public getMeetingMinutes(mediaId: string): Promise<MeetingMinutesView> {
    return get(`${this._resource}/${mediaId}/meeting-minutes`, {}, { skipNotFoundRedirect: true });
  }

  public async getMeetingMinutesResult(mediaId: string): Promise<MeetingMinutesResult> {
    const raw = await get(`/source/${mediaId}/meeting-minutes`, {}, { skipNotFoundRedirect: true });
    return normalizeKeys(raw) as MeetingMinutesResult;
  }

  public enableMeetingMinutes(mediaId: string): Promise<OperationEntity> {
    return this.action({ id: mediaId }, 'meeting-minutes/enable');
  }

  public disableMeetingMinutes(mediaId: string): Promise<OperationEntity> {
    return this.action({ id: mediaId }, 'meeting-minutes/disable');
  }

  public async getThumbnailsFromPreview(previewUrl: string): Promise<TimedataThumbnail[]> {
    const vttResponse = await axios.get(previewUrl);
    return decodeVtt(vttResponse.data) as TimedataThumbnail[];
  }
}

function pascalToSnake(key: string): string {
  return key.replace(/([A-Z])/g, (_m, c: string, i: number) =>
    i === 0 ? c.toLowerCase() : `_${c.toLowerCase()}`
  );
}

function normalizeKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeKeys);
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      out[pascalToSnake(key)] = normalizeKeys((value as Record<string, unknown>)[key]);
    }
    return out;
  }
  return value;
}
